/**
 * TrackerStateBadge — the one honest read of an element's tracker state:
 * live on the tracker, DELETED from it (with the real deletion time — stamped
 * only when a real tracker write happened), or never pushed at all.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { Check, X } from 'lucide-react';

import { formatDateTimeGb } from '@/lib/utils';

export type TrackerState = 'live' | 'removed' | 'never';

/** Pure derivation so tables can sort/filter on it too. */
export function trackerState(
  added: boolean,
  removedAt?: string | null,
): TrackerState {
  if (added) return 'live';
  return removedAt ? 'removed' : 'never';
}

export interface TrackerStateBadgeProps {
  added: boolean;
  /** When a REAL tracker write deleted the element (null = never happened). */
  removedAt?: string | null;
  /** Label for the live state (e.g. "On tracker", "Live"). */
  liveLabel?: string;
}

export const TrackerStateBadge: FC<TrackerStateBadgeProps> = ({
  added,
  removedAt,
  liveLabel = 'On tracker',
}) => {
  const state = trackerState(added, removedAt);
  if (state === 'live') {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-medium text-success">
        <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {liveLabel}
      </span>
    );
  }
  if (state === 'removed') {
    return (
      <span
        className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] text-destructive/80"
        title={`Deleted from the tracker ${formatDateTimeGb(removedAt ?? null)}`}
      >
        <X className="h-3.5 w-3.5 shrink-0" aria-hidden />
        Deleted {formatDateTimeGb(removedAt ?? null)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] text-muted-foreground">
      <X className="h-3.5 w-3.5 shrink-0" aria-hidden />
      Never pushed
    </span>
  );
};
